import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, orderBy } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Dialog } from '@headlessui/react';
import { FileSearch, Download, Clock, CheckCircle, Loader2, BookOpen, AlertCircle } from 'lucide-react';
import { AUDIT_REPORT_CAMPAIGN, AUDIT_REPORT_FEES, auditReportPrice } from '../../utils/auditCampaign';

const formatSEK = (amount) =>
  new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' kr';

export default function AuditReportOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [associationId, setAssociationId] = useState(null);
  const [associationName, setAssociationName] = useState('');
  const [apartmentCount, setApartmentCount] = useState(0);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderState, setOrderState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const userSnap = await getDoc(doc(db, 'users', uid));
      if (!userSnap.exists()) return;
      const userData = userSnap.data();
      setAssociationId(userData.associationId);

      if (userData.associationId) {
        const assocSnap = await getDoc(doc(db, 'associations', userData.associationId));
        let manualCount = null;
        if (assocSnap.exists()) {
          const assocData = assocSnap.data();
          setAssociationName(assocData.name || '');
          if (typeof assocData.numberOfApartments === 'number' && assocData.numberOfApartments > 0) {
            manualCount = assocData.numberOfApartments;
          }
        }
        if (manualCount !== null) {
          setApartmentCount(manualCount);
        } else {
          const housingSnap = await getDocs(query(
            collection(db, 'housing'),
            where('associationId', '==', userData.associationId)
          ));
          setApartmentCount(housingSnap.size);
        }
      }

      const q = query(
        collection(db, 'auditReportOrders'),
        where('associationId', '==', userData.associationId)
      );
      const snap = await getDocs(q);
      const fetched = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.requestedAt || '').localeCompare(a.requestedAt || ''));
      setOrders(fetched);
    } catch (err) {
      console.error('Error fetching audit report orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const openOrderModal = () => {
    setOrderState('idle');
    setErrorMessage('');
    setShowOrderModal(true);
  };

  const confirmOrder = async () => {
    if (!associationId) return;
    setOrderState('loading');
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const requestAuditReport = httpsCallable(functions, 'requestAuditReport');
      const result = await requestAuditReport({ associationId });

      // Under en kampanj märks beställningen så att Hyria-admin ser att den
      // inte ska faktureras. Beställningen finns oavsett om märkningen lyckas.
      const orderId = result?.data?.orderId;
      if (price.free && orderId) {
        try {
          await updateDoc(doc(db, 'auditReportOrders', orderId), {
            campaignId: price.campaignId,
            price: 0,
            priceNote: AUDIT_REPORT_CAMPAIGN.title,
          });
        } catch (flagErr) {
          console.error('Kunde inte märka beställningen som kampanj:', flagErr);
        }
      }

      await fetchOrders();
      setOrderState('success');
    } catch (err) {
      console.error('Error requesting audit report:', err);
      setErrorMessage('Kunde inte beställa rapport. Försök igen.');
      setOrderState('error');
    }
  };

  const handleMarkRead = async (orderId) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'auditReportOrders', orderId), {
        [`unreadBy.${uid}`]: false
      });
      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, unreadBy: { ...o.unreadBy, [uid]: false } } : o
      ));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const uid = auth.currentUser?.uid;
  // Priset i dag — noll under kampanjen, ordinarie annars. Datumet avgör, inte en flagga.
  const price = auditReportPrice(apartmentCount);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#62B8E9] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#222222] font-['Poppins']">Avvikelserapport</h1>
        <p className="text-sm text-[#626262] mt-1 mb-4">{associationName}</p>
        {price.free && (
          <div className="inline-flex items-center px-3 py-1.5 mb-4 text-sm font-medium bg-green-100 text-green-800 rounded-full">
            Kostnadsfri till och med den 30 september 2026
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={openOrderModal}
            className="px-6 py-3 bg-[#62B8E9] text-white rounded-xl font-medium hover:bg-[#4A9FD1] transition-colors flex items-center gap-2"
          >
            <FileSearch className="w-5 h-5" />
            Beställ avvikelserapport
          </button>
          <a
            href="/steg-for-steg-avvikelserapport.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 border border-zinc-300 text-zinc-700 rounded-xl font-medium hover:bg-zinc-50 transition-colors flex items-center gap-2"
          >
            <BookOpen className="w-5 h-5" />
            Steg-för-steg-guide
          </a>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
          <FileSearch className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-500">Inga avvikelserapporter ännu.</p>
          <p className="text-zinc-400 text-sm mt-1">Klicka på knappen ovan för att beställa er första rapport.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const isUnread = order.unreadBy?.[uid] === true;
            const isCompleted = order.status === 'completed';
            const dateStr = order.requestedAt
              ? new Date(order.requestedAt).toLocaleDateString('sv-SE')
              : '';
            const completedDateStr = order.completedAt
              ? new Date(order.completedAt).toLocaleDateString('sv-SE')
              : '';

            return (
              <div
                key={order.id}
                className={`bg-white rounded-2xl border p-6 transition-colors ${
                  isUnread ? 'border-[#62B8E9] bg-blue-50/30' : 'border-zinc-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {isCompleted ? (
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[#222222]">
                          Avvikelserapport
                        </span>
                        {isUnread && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">
                            Ny
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#626262]">
                        Beställd {dateStr}
                        {isCompleted && ` — Klar ${completedDateStr}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {isCompleted ? (
                      <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Klar
                      </span>
                    ) : (
                      <span className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                        Beställd
                      </span>
                    )}
                    {isCompleted && order.reportUrl && (
                      <a
                        href={order.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => isUnread && handleMarkRead(order.id)}
                        className="px-4 py-2 bg-[#62B8E9] text-white rounded-xl text-sm font-medium hover:bg-[#4A9FD1] transition-colors flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Ladda ner
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={showOrderModal}
        onClose={() => orderState !== 'loading' && setShowOrderModal(false)}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
        <Dialog.Panel className="relative bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          {orderState === 'idle' && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#E8F5FB] rounded-full flex items-center justify-center">
                  <FileSearch className="w-5 h-5 text-[#62B8E9]" />
                </div>
                <Dialog.Title className="text-xl font-bold text-[#222222]">
                  Bekräfta beställning
                </Dialog.Title>
              </div>
              <p className="text-sm text-[#626262] mb-6">
                Avvikelserapport för {associationName}
              </p>

              {price.free ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-5" data-testid="audit-campaign-box">
                  <p className="text-sm font-bold text-green-900">{AUDIT_REPORT_CAMPAIGN.title}</p>
                  <p className="text-sm text-green-900 mt-1">{AUDIT_REPORT_CAMPAIGN.text}</p>
                  <p className="text-xs text-green-800 mt-1">{AUDIT_REPORT_CAMPAIGN.validUntil}</p>
                  <div className="space-y-3 border-t border-green-200 mt-4 pt-4">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-[#626262]">Grundavgift</span>
                      <span className="text-sm font-medium text-[#222222] tabular-nums">0 kr</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-sm text-[#626262]">Rörlig avgift</div>
                        <div className="text-xs text-zinc-400">{apartmentCount} lägenheter</div>
                      </div>
                      <span className="text-sm font-medium text-[#222222] tabular-nums">0 kr</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-green-200 mt-4 pt-4">
                    <span className="text-base font-bold text-[#222222]">Totalt</span>
                    <span className="text-2xl font-bold text-green-700 tabular-nums">0 kr</span>
                  </div>
                </div>
              ) : (
                <div className="bg-[#F8FAFC] border border-zinc-200 rounded-xl p-5 mb-5">
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-[#626262]">Grundavgift</span>
                      <span className="text-sm font-medium text-[#222222] tabular-nums">
                        {formatSEK(price.base)}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <div>
                        <div className="text-sm text-[#626262]">Rörlig avgift</div>
                        <div className="text-xs text-zinc-400">
                          {apartmentCount} lägenheter × {formatSEK(AUDIT_REPORT_FEES.perApartment)}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-[#222222] tabular-nums">
                        {formatSEK(price.variable)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-zinc-200 mt-4 pt-4">
                    <span className="text-base font-bold text-[#222222]">Totalt</span>
                    <span className="text-2xl font-bold text-[#62B8E9] tabular-nums">
                      {formatSEK(price.total)}
                    </span>
                  </div>
                </div>
              )}

              <p className="text-sm text-[#626262] mb-6 text-center">
                Vänligen bekräfta er beställning här.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1 px-4 py-3 border border-zinc-300 text-zinc-700 rounded-xl font-medium hover:bg-zinc-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={confirmOrder}
                  disabled={apartmentCount === 0}
                  className="flex-1 px-4 py-3 bg-[#62B8E9] text-white rounded-xl font-medium hover:bg-[#4A9FD1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Bekräfta beställning
                </button>
              </div>
            </>
          )}

          {orderState === 'loading' && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="w-10 h-10 text-[#62B8E9] animate-spin" />
              <p className="text-sm text-[#626262]">Skickar beställning...</p>
            </div>
          )}

          {orderState === 'success' && (
            <div className="py-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <Dialog.Title className="text-xl font-bold text-[#222222] mb-2">
                Tack för din beställning!
              </Dialog.Title>
              <p className="text-sm text-[#626262] mb-6 max-w-xs">
                Ni finner er rapport här inom två arbetsdagar.
              </p>
              <button
                onClick={() => setShowOrderModal(false)}
                className="px-6 py-3 bg-[#62B8E9] text-white rounded-xl font-medium hover:bg-[#4A9FD1] transition-colors"
              >
                Stäng
              </button>
            </div>
          )}

          {orderState === 'error' && (
            <div className="py-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <Dialog.Title className="text-xl font-bold text-[#222222] mb-2">
                Något gick fel
              </Dialog.Title>
              <p className="text-sm text-[#626262] mb-6 max-w-xs">{errorMessage}</p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1 px-4 py-3 border border-zinc-300 text-zinc-700 rounded-xl font-medium hover:bg-zinc-50 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  onClick={confirmOrder}
                  className="flex-1 px-4 py-3 bg-[#62B8E9] text-white rounded-xl font-medium hover:bg-[#4A9FD1] transition-colors"
                >
                  Försök igen
                </button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}
